import React, { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Line, Circle } from 'react-native-svg';
import { FontFamily } from '../../../../../constants/fonts';
import { CrewingHorizontalScrollView } from './webScrollStyles';

export interface SchedulingTrendData {
  periods: string[];
  // Secondary line under each x-axis tick, e.g. "'26" for a period, matching Perfect Truck Execution's ticks
  periodSubLabels?: (string | undefined)[];
  accuracy: number[];
  efficiency: number[];
  compliance: number[];
  kcphIndex: number[];
}

interface SchedulingTrendCardProps {
  title: string;
  subtitle?: string;
  description?: string;    
  data: SchedulingTrendData;
}

// Minimum horizontal room per data point — once pointCount * this exceeds the card's
// width (e.g. a day-level breakdown with 30+ points) the chart scrolls instead of
// squeezing every label into the fixed viewport.
const MIN_POINT_SPACING = 56;
const X_AXIS_ROW_HEIGHT = 36;
const WEB_SCROLLBAR_HEIGHT = 20;
const TOOLTIP_GAP = 8;
const TOOLTIP_ESTIMATED_HEIGHT = 30;

const SchedulingTrendCard: React.FC<SchedulingTrendCardProps> = ({
  title,
  subtitle,
  description,
  data,
}) => {
  const { periods, periodSubLabels, accuracy, efficiency, compliance, kcphIndex } = data;
  
  // Check for no data condition
  const hasData = periods.length > 0 && 
    (accuracy.length > 0 || efficiency.length > 0 || compliance.length > 0 || kcphIndex.length > 0);
  
  if (!hasData) {
    return (
      <View style={styles.card}>
        <View style={[styles.headerRow, Platform.OS === 'web' && styles.headerRowWeb, Platform.OS === 'ios' && styles.headerRowIos]}>
          <View style={[styles.titleRow, Platform.OS === 'web' && styles.titleRowWeb, Platform.OS === 'ios' && styles.titleRowIos]}>
            <Text style={styles.title}>{title}</Text>
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </View>
          {description && <Text style={[styles.description, Platform.OS === 'web' && styles.descriptionWeb, Platform.OS === 'ios' && styles.descriptionIos]}>{description}</Text>}
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No scheduling data available</Text>
        </View>
      </View>
    );
  }

  const pointCount = Math.max(periods.length, accuracy.length, efficiency.length, compliance.length, kcphIndex.length, 1);
  const [plotAreaWidth, setPlotAreaWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [selectedPoint, setSelectedPoint] = useState<{ seriesKey: string; index: number } | null>(null);
  // Fill whatever vertical space the card actually has instead of a fixed 190px, minus room for the x-axis label row.

  // Fixed spacing per point once it would no longer fit the visible plot area — otherwise
  // spread points evenly across the available width (matches the previous fill-to-width look).
  const chartWidth = useMemo(() => {
    const minFromPoints = pointCount * MIN_POINT_SPACING;
    if (plotAreaWidth <= 0) return minFromPoints;
    return Math.max(plotAreaWidth, minFromPoints);
  }, [plotAreaWidth, pointCount]);
  const needsHScroll = plotAreaWidth > 0 && chartWidth > plotAreaWidth;
  const scrollbarHeight = Platform.OS === 'web' && needsHScroll ? WEB_SCROLLBAR_HEIGHT : 0;
  const chartHeight = Platform.OS === 'web' && viewportHeight > 0
    ? Math.max(0, viewportHeight - X_AXIS_ROW_HEIGHT - scrollbarHeight)
    : Math.max(190, viewportHeight - X_AXIS_ROW_HEIGHT);
  const ChartContainer = Platform.OS === 'web' ? View : ScrollView;
  const pointSpacing = chartWidth / pointCount;
  
  // Calculate dynamic Y-axis range based on data
  const allValues = [accuracy, efficiency, compliance, kcphIndex].flat().filter((v) => typeof v === 'number' && !isNaN(v));
  const dataMin = allValues.length > 0 ? Math.min(...allValues) : 80;
  const dataMax = allValues.length > 0 ? Math.max(...allValues) : 100;
  const dataRange = Math.max(1, dataMax - dataMin);
  const padding = Math.max(5, dataRange * 0.15); // 15% padding or minimum 5 units
  
  const yMin = Math.floor((dataMin - padding) / 5) * 5; // Round down to nearest 5
  const yMax = Math.ceil((dataMax + padding) / 5) * 5; // Round up to nearest 5

  const toY = (value: number) => {
    const clamped = Math.max(yMin, Math.min(yMax, value));
    return ((yMax - clamped) / (yMax - yMin)) * chartHeight;
  };
  const getTooltipLeft = (pointX: number, tooltipWidth: number) => {
    const hitLeft = pointX - 14;
    return Math.max(4 - hitLeft, Math.min(14 - tooltipWidth / 2, chartWidth - tooltipWidth - 4 - hitLeft));
  };

  const toBarHeight = (value: number) => {
    const clamped = Math.max(yMin, Math.min(yMax, value));
    return ((clamped - yMin) / (yMax - yMin)) * chartHeight;
  };

  const normalizeSeries = (series: number[]) => {
    if (series.length >= pointCount) return series.slice(0, pointCount);
    const seed = series.length > 0 ? series[series.length - 1] : yMin;
    return [...series, ...Array.from({ length: pointCount - series.length }, () => seed)];
  };

  const normalizedAccuracy = useMemo(() => normalizeSeries(accuracy), [accuracy, pointCount]);
  const normalizedEfficiency = useMemo(() => normalizeSeries(efficiency), [efficiency, pointCount]);
  const normalizedCompliance = useMemo(() => normalizeSeries(compliance), [compliance, pointCount]);
  const normalizedKcph = useMemo(() => normalizeSeries(kcphIndex), [kcphIndex, pointCount]);

  const xAt = (index: number) => (index + 0.5) * pointSpacing;

  // Labels top-to-bottom must mirror the gridlines (top = yMax, bottom = yMin), not a fixed 100/95/90/85/80 scale.
  const yAxisTicks = useMemo(() => {
    const step = (yMax - yMin) / 4;
    return Array.from({ length: 5 }, (_, i) => yMax - i * step);
  }, [yMin, yMax]);

  const lineSeries = [
    { key: 'accuracy', label: 'Crewing Accuracy', color: '#005CBC', values: normalizedAccuracy },
    { key: 'efficiency', label: 'Crewing Efficiency', color: '#E58A21', values: normalizedEfficiency },
    { key: 'compliance', label: 'Crewing Compliance', color: '#18A6A6', values: normalizedCompliance },
  ] as const;

  return (
    <View style={styles.card}>
      <View style={[styles.headerRow, Platform.OS === 'web' && styles.headerRowWeb, Platform.OS === 'ios' && styles.headerRowIos]}>
        <View style={[styles.titleRow, Platform.OS === 'web' && styles.titleRowWeb, Platform.OS === 'ios' && styles.titleRowIos]}>
          <Text style={styles.title}>{title}</Text>
          {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        </View>
        {description && <Text style={[styles.description, Platform.OS === 'web' && styles.descriptionWeb, Platform.OS === 'ios' && styles.descriptionIos]}>{description}</Text>}
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        {lineSeries.map((series) => (
          <View key={series.key} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: series.color }]} />
            <Text style={styles.legendText}>{series.label}</Text>
          </View>
        ))}
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: '#A6D8FF', borderWidth: 1, borderColor: '#4A87C6' }]} />
          <Text style={styles.legendText}>KCPH Index</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, styles.legendTargetDot]} />
          <Text style={styles.legendText}>Scheduling Target 95%</Text>
        </View>
      </View>

      <View
        style={styles.chartViewport}
        onLayout={(event) => {
          const nextHeight = event.nativeEvent.layout.height;
          if (nextHeight > 0 && Math.abs(nextHeight - viewportHeight) > 1) setViewportHeight(nextHeight);
        }}
      >
        <ChartContainer {...(Platform.OS === 'web' ? {} : { nestedScrollEnabled: true, showsVerticalScrollIndicator: false, scrollEnabled: false })}>
        <View style={styles.chartContainer}>
          <View style={styles.axisRow}>
            <View style={[styles.yAxisLabels, { height: chartHeight }]}>
              {yAxisTicks.map((tick, index) => (
                <Text key={index} style={styles.yAxisLabel}>{`${Math.round(tick)}%`}</Text>
              ))}
            </View>

            <View
              style={[styles.plotRegion, Platform.OS === 'web' && styles.plotRegionWeb]}
              onLayout={(event) => {
                const nextWidth = event.nativeEvent.layout.width;
                if (nextWidth > 0 && Math.abs(nextWidth - plotAreaWidth) > 1) setPlotAreaWidth(nextWidth);
              }}
            >
            <CrewingHorizontalScrollView
              horizontal
              nestedScrollEnabled
              scrollEnabled={needsHScroll}
              showsHorizontalScrollIndicator={needsHScroll}
            >
            <View>
            <View
              style={[styles.chartArea, { height: chartHeight, width: chartWidth }]}
            >
              <View style={[styles.gridLine, { top: 0 }]} />
              <View style={[styles.gridLine, { top: chartHeight * 0.25 }]} />
              <View style={[styles.gridLine, { top: chartHeight * 0.5 }]} />
              <View style={[styles.gridLine, { top: chartHeight * 0.75 }]} />
              <View style={[styles.gridLine, { top: chartHeight }]} />
              <View style={[styles.targetLine, { top: toY(95) }]} />

              <View style={styles.dataGrid}>
                {periods.map((period, index) => (
                  <View key={period} style={[styles.dataColumn, { width: pointSpacing }]}>
                    <View style={[styles.kcphBar, { height: toBarHeight(normalizedKcph[index] ?? yMin) }]} />
                  </View>
                ))}
              </View>

              <Svg
                width={chartWidth}
                height={chartHeight}
                style={styles.svgOverlay}
                pointerEvents="none"
              >
                {lineSeries.map((series) =>
                  series.values.slice(1).map((value, index) => {
                    const x1 = xAt(index);
                    const x2 = xAt(index + 1);
                    const y1 = toY(series.values[index] ?? yMin);
                    const y2 = toY(value ?? yMin);
                    return (
                      <Line
                        key={`${series.key}-line-${index}`}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={series.color}
                        strokeWidth={2}
                      />
                    );
                  }),
                )}

                {lineSeries.map((series) =>
                  series.values.map((value, index) => {
                    const cx = xAt(index);
                    return (
                      <Circle
                        key={`${series.key}-point-${index}`}
                        cx={cx}
                        cy={toY(value ?? yMin)}
                        r={3.2}
                        fill={series.color}
                        stroke="#FFFFFF"
                        strokeWidth={1.2}
                      />
                    );
                  }),
                )}
              </Svg>
              {[
                { key: 'accuracy', label: 'Accuracy', values: normalizedAccuracy },
                { key: 'efficiency', label: 'Efficiency', values: normalizedEfficiency },
                { key: 'compliance', label: 'Compliance', values: normalizedCompliance },
                { key: 'kcph', label: 'KCPH', values: normalizedKcph },
              ].map((series) => series.values.map((value, index) => {
                const pointX = xAt(index);
                const pointY = toY(value ?? 0);
                const showAbove = pointY >= TOOLTIP_ESTIMATED_HEIGHT + TOOLTIP_GAP + 14;
                const isSelected = selectedPoint?.seriesKey === series.key && selectedPoint.index === index;
                const tooltipValue = `${(value ?? 0).toFixed(1)}%`;
                const tooltipWidth = Math.max(40, tooltipValue.length * 7 + 16);
                return (
                  <Pressable
                    key={`${series.key}-${index}`}
                    onPress={() => setSelectedPoint((previous) => isSelected ? null : { seriesKey: series.key, index })}
                    onHoverIn={() => setSelectedPoint({ seriesKey: series.key, index })}
                    onHoverOut={() => setSelectedPoint((previous) => previous?.seriesKey === series.key && previous.index === index ? null : previous)}
                    style={[styles.pointHitArea, { left: pointX - 14, top: Math.max(0, pointY - 14) }]}
                  >
                    {isSelected ? (
                      <View style={[styles.tooltip, { width: tooltipWidth, left: getTooltipLeft(pointX, tooltipWidth), ...(showAbove ? { bottom: 22 + TOOLTIP_GAP } : { top: 22 + TOOLTIP_GAP }) }]}> 
                        <Text style={styles.tooltipRow}>{tooltipValue}</Text>
                      </View>
                    ) : null}
                  </Pressable>
                );
              }))}
            </View>

            <View style={[styles.xAxisLabels, { width: chartWidth }]}>
              {periods.map((period, index) => (
                <View key={`${period}-${index}`} style={[styles.xAxisLabelCell, { width: pointSpacing }]}>
                  <Text style={styles.xAxisLabel}>{period}</Text>
                  {periodSubLabels?.[index] ? (
                    <Text style={styles.xAxisSubLabel}>{periodSubLabels[index]}</Text>
                  ) : null}
                </View>
              ))}
            </View>
            </View>
            </CrewingHorizontalScrollView>
            </View>
          </View>
        </View>
        </ChartContainer>
      </View>


    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    flex: 1,
    minHeight: 360,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 10,
    flexWrap: 'wrap',
    width: '100%',
  },
  titleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 4,
    width: '100%',
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
  },
  description: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
    width: '100%',
    marginTop: 4,
  },
  headerRowWeb: {
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  titleRowWeb: {
    flex: 1,
    width: 'auto',
    flexWrap: 'nowrap',
    columnGap: 4,
  },
  descriptionWeb: {
    width: 'auto',
    marginTop: 0,
    marginLeft: 12,
    flexShrink: 1,
    textAlign: 'right',
  },
  headerRowIos: {
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  titleRowIos: {
    flex: 1,
    width: 'auto',
    flexWrap: 'nowrap',
    columnGap: 4,
  },
  descriptionIos: {
    width: 'auto',
    marginTop: 0,
    marginLeft: 8,
    flexShrink: 0,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 10,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendText: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  chartViewport: {
    flex: 1,
    marginTop: 4,
    marginBottom: 10,
  },
  chartContainer: {},
  axisRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  yAxisLabels: {
    height: 190,
    justifyContent: 'space-between',
    paddingRight: 8,
    width: 34,
  },
  yAxisLabel: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    textAlign: 'right',
  },
  chartArea: {
    height: 190,
    position: 'relative',
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#D5DFED',
    backgroundColor: 'transparent',
  },
  plotRegion: {
    flex: 1,
  },
  plotRegionWeb: {
    minWidth: 0,
    overflow: 'hidden',
  },
  gridLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: '#E9EFF8',
  },
  svgOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
  pointHitArea: {
    position: 'absolute',
    top: 0,
    width: 28,
    height: 28,
    zIndex: 2,
  },
  tooltip: {
    position: 'absolute',
    left: -54,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    backgroundColor: '#1E2127',
    zIndex: 3,
  },
  tooltipRow: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#FFFFFF',
  },
  targetLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: '#9EB7D6',
    borderStyle: 'dashed',
  },
  legendTargetDot: {
    height: 0,
    borderRadius: 0,
    borderTopWidth: 2,
    borderTopColor: '#9EB7D6',
    borderStyle: 'dashed',
  },
  dataGrid: {
    flexDirection: 'row',
    height: '100%',
  },
  dataColumn: {
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  kcphBar: {
    width: '66%',
    minHeight: 2,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    backgroundColor: '#A6D8FF',
    opacity: 0.7,
  },
  xAxisLabels: {
    flexDirection: 'row',
    minHeight: 32,
    paddingBottom: 4,
  },
  xAxisLabelCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  xAxisLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#777777',
    textAlign: 'center',
  },
  xAxisSubLabel: {
    fontSize: 10,
    lineHeight: 14,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#9CA3AF',
    textAlign: 'center',
  },
  infoText: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    marginTop: 4,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#9CA3AF',
    fontWeight: '500',
  },
});

export default SchedulingTrendCard;
